import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError } from '@familywallet/api-client';
import {
  type Account,
  type Category,
  type CreateTransactionRequest,
  type Payee,
  parseRateInput,
  type RecordableKind,
  type RecurringTemplate,
  sortCurrencies,
  type Transaction,
  type TransactionDefaults,
} from '@familywallet/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { type FieldError, type Resolver, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { AmountPreview } from '../components/AmountPreview';
import { LimitWarning } from '../components/PlanningCards';
import { CurrencyLockNotice } from '../components/CurrencyLockNotice';
import { FormError } from '../components/FormError';
import { PageHeader } from '../components/PageHeader';
import { Chip, Row, SwapRow } from '../components/FormRow';
import { PickerSheet, type PickerOption } from '../components/PickerSheet';
import { StaleRateNotice } from '../components/StaleRateNotice';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, ListGroup } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Segmented } from '../components/ui/segmented';
import {
  type AddFormValues,
  addFormSchema,
  creditRemaining,
  fromTransaction,
  parseNonNegative,
  parsePositive,
  splitRemaining,
} from '../lib/add-form';
import { api } from '../lib/api';
import { useCan, useMe } from '../lib/auth';
import { formatDay, todayIn } from '../lib/dates';
import {
  categoriesKey,
  latestRateMap,
  useAccounts,
  useCategories,
  useCurrentRates,
  usePayees,
  useRecurring,
  useTransaction,
  useTransactionDefaults,
} from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { useIdempotencyKey } from '../lib/members';
import { formatAmount, formatRateValue, groupAmountInput } from '../lib/money';
import { cn } from '../lib/utils';
import { ExchangeForm } from './ExchangeForm';
import { ForeignExpenseForm } from './ForeignExpenseForm';
import { PersonPicker } from './NewDebtPage';

const KINDS = ['expense', 'income', 'transfer', 'exchange'] as const;
type AddKind = (typeof KINDS)[number];

function isKind(value: string | null): value is AddKind {
  return (KINDS as readonly string[]).includes(value ?? '');
}

/**
 * The Add screen: expense, income (with splits), transfer, or exchange, in any currency the
 * account holds. An expense or income can also be paid with several payments, in several
 * currencies and accounts (bill, payments, returned).
 */
export function AddPage() {
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const me = useMe();
  const accounts = useAccounts();
  const defaults = useTransactionDefaults();
  const rates = useCurrentRates();
  const kindParam = params.get('kind');
  const kind: AddKind = isKind(kindParam) ? kindParam : 'expense';
  const foreign = (kind === 'expense' || kind === 'income') && params.get('foreign') === '1';
  // "Record" on a recurring occurrence: /add?kind=…&recurring=<template>&due=<date>.
  const recurringId = params.get('recurring');
  const dueOn = params.get('due');
  const recurring = useRecurring(recurringId !== null);
  const template = recurringId
    ? recurring.data?.items.find((r) => r.id === recurringId)
    : undefined;
  const prefill = template && dueOn ? { template, dueOn } : undefined;
  // Correct on an expense or income partly on credit: /add?kind=…&correct=<transaction>.
  const correctId = params.get('correct');
  const correcting = useTransaction(correctId ?? '');

  if (
    !me.data ||
    !accounts.data ||
    !defaults.data ||
    !rates.data ||
    (recurringId !== null && recurring.isPending) ||
    (correctId !== null && correcting.isPending)
  ) {
    if (accounts.isError || defaults.isError || rates.isError || correcting.isError) {
      return (
        <Card role="alert">
          <p>{t('common.loadFailed')}</p>
        </Card>
      );
    }
    return <p role="status">{t('app.loading')}</p>;
  }
  const recordable = accounts.data.items.filter((a) => a.canRecord && a.archivedAt === null);
  const original =
    correctId !== null &&
    correcting.data &&
    (correcting.data.kind === 'expense' || correcting.data.kind === 'income')
      ? correcting.data
      : undefined;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={original ? t('credit.correctTitle') : t('add.title')}
        back={original ? `/activity/${original.id}` : undefined}
      />
      {original && (
        <p className="rounded-3xl border border-line bg-surface px-4 py-3 text-sm">
          {t('credit.correctHint')}
        </p>
      )}
      {prefill && (
        <p className="rounded-3xl border border-line bg-surface px-4 py-3 text-sm">
          {t('recurring.recordingFor', {
            name: prefill.template.name,
            date: formatDay(prefill.dueOn, todayIn(me.data.family.timezone), i18n.language, {
              today: t('activity.today'),
              yesterday: t('activity.yesterday'),
            }),
          })}
        </p>
      )}
      {!prefill && !original && (
        <Segmented
          label={t('add.kindLabel')}
          options={KINDS.map((k) => ({ value: k, label: t(`add.kinds.${k}`) }))}
          value={kind}
          onChange={(k) => setParams({ kind: k }, { replace: true })}
          compact
        />
      )}
      {(kind === 'expense' || kind === 'income') &&
        !prefill &&
        !original &&
        (recordable.length > 1 || me.data.family.currencies.length > 1) && (
          <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-3xl border border-line bg-surface px-4 py-3">
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="font-medium">{t('foreign.toggle')}</span>
              <span id="foreign-toggle-hint" className="text-sm text-muted">
                {t(`foreign.toggleHint_${kind}`)}
              </span>
            </span>
            <input
              type="checkbox"
              role="switch"
              aria-label={t('foreign.toggle')}
              aria-describedby="foreign-toggle-hint"
              className="size-5 shrink-0 accent-accent"
              checked={foreign}
              onChange={() =>
                setParams(foreign ? { kind } : { kind, foreign: '1' }, { replace: true })
              }
            />
          </label>
        )}
      {recordable.length === 0 ? (
        <Card className="flex flex-col gap-4">
          <p className="text-muted">{t('add.noAccounts')}</p>
          <Link to="/accounts/new" className={buttonVariants()}>
            {t('add.noAccountsAction')}
          </Link>
        </Card>
      ) : kind === 'exchange' ? (
        <ExchangeForm
          accounts={recordable}
          defaults={defaults.data}
          baseCurrency={me.data.family.baseCurrency}
          timezone={me.data.family.timezone}
          latestRates={latestRateMap(rates.data)}
          myId={me.data.member.id}
        />
      ) : foreign ? (
        <ForeignExpenseForm
          key={`${original?.kind ?? kind}-${original?.id ?? ''}`}
          kind={original ? (original.kind as 'expense' | 'income') : (kind as 'expense' | 'income')}
          correcting={original}
          accounts={recordable}
          defaults={defaults.data}
          baseCurrency={me.data.family.baseCurrency}
          timezone={me.data.family.timezone}
          latestRates={latestRateMap(rates.data)}
          myId={me.data.member.id}
          familyCurrencies={me.data.family.currencies}
        />
      ) : (
        <AddForm
          key={`${kind}-${prefill?.dueOn ?? ''}-${original?.id ?? ''}`}
          kind={
            original
              ? (original.kind as 'expense' | 'income')
              : prefill
                ? prefill.template.kind
                : kind
          }
          prefill={prefill}
          correcting={original}
          accounts={recordable}
          defaults={defaults.data}
          baseCurrency={me.data.family.baseCurrency}
          timezone={me.data.family.timezone}
          latestRates={latestRateMap(rates.data)}
          myId={me.data.member.id}
        />
      )}
    </div>
  );
}

/** Currencies an account holds, base first (the API already sorts them). */
const currenciesOf = (account: Account | undefined) =>
  account?.balances.map((b) => b.currency) ?? [];

function preferredCurrency(
  options: readonly string[],
  accountId: string,
  defaults: TransactionDefaults,
): string {
  const last = defaults.lastCurrencyByAccount[accountId];
  return last && options.includes(last) ? last : (options[0] ?? '');
}

function AddForm({
  kind,
  prefill,
  correcting,
  accounts,
  defaults,
  baseCurrency,
  timezone,
  latestRates,
  myId,
}: {
  kind: RecordableKind;
  /** Recording an occurrence of a recurring template: its terms fill the form. */
  prefill?: { template: RecurringTemplate; dueOn: string };
  /** Correct: the expense or income being corrected fills the form; saving replaces it. */
  correcting?: Transaction;
  accounts: Account[];
  defaults: TransactionDefaults;
  baseCurrency: string;
  timezone: string;
  latestRates: Record<string, string>;
  myId: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateLedger();
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const today = todayIn(timezone);
  const byId = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  // Mine first (the list already puts them first), then shared, then others'.
  const firstMine = accounts.find((a) => a.ownerMemberId === myId) ?? accounts[0]!;
  const remembered = defaults.lastAccountId[kind];
  const fromTemplate = prefill && byId.has(prefill.template.account.id);
  const original = correcting ? fromTransaction(correcting) : null;
  const startAccount =
    original?.accountId && byId.has(original.accountId)
      ? original.accountId
      : fromTemplate
        ? prefill!.template.account.id
        : remembered && byId.has(remembered)
          ? remembered
          : firstMine.id;
  const templateTo = prefill?.template.toAccount?.id;
  const startTo =
    templateTo && byId.has(templateTo)
      ? templateTo
      : (accounts.find((a) => a.id !== startAccount)?.id ?? '');
  const startCurrency = original
    ? original.currency
    : prefill && currenciesOf(byId.get(startAccount)).includes(prefill.template.currency)
      ? prefill.template.currency
      : preferredCurrency(currenciesOf(byId.get(startAccount)), startAccount, defaults);

  // Shown when there is no family rate yet, the API asked for one (no rate on that date), or
  // the person wants a money changer's rate for this one.
  const [rateWanted, setRateWanted] = useState(false);
  const [picker, setPicker] = useState<
    | { type: 'category'; line: number | null }
    | { type: 'account'; field: 'accountId' | 'fromAccountId' | 'toAccountId' }
    | null
  >(null);
  const [payeeFocused, setPayeeFocused] = useState(false);
  const [personPicker, setPersonPicker] = useState(false);
  const [personLabel, setPersonLabel] = useState(original?.personLabel ?? '');
  const canOutside = useCan('debts.outside');
  const canManagePeople = useCan('people.manage');
  const canAddPeople = canOutside || canManagePeople;
  const [categoryTouched, setCategoryTouched] = useState(false);

  const resolverRef = useRef<Resolver<AddFormValues, unknown, CreateTransactionRequest> | null>(
    null,
  );
  const form = useForm<AddFormValues, unknown, CreateTransactionRequest>({
    resolver: (values, context, options) => resolverRef.current!(values, context, options),
    defaultValues: {
      amount:
        prefill?.template.amount && prefill.template.currency === startCurrency
          ? groupAmountInput(prefill.template.amount, startCurrency)
          : '',
      currency: startCurrency,
      accountId: startAccount,
      fromAccountId: startAccount,
      toAccountId: startTo,
      categoryId: prefill?.template.category?.id ?? '',
      split: false,
      lines: [],
      payeeName: prefill?.template.payee?.name ?? '',
      occurredOn: prefill && prefill.dueOn < today ? prefill.dueOn : today,
      description: prefill?.template.description ?? '',
      sharedWithFamily: false,
      rate: '',
      credit: false,
      paidNow: '',
      personId: '',
      personName: '',
      dueOn: '',
      ...original?.values,
    },
  });
  const v = useWatch({ control: form.control }) as AddFormValues;
  const lines = useFieldArray({ control: form.control, name: 'lines' });

  const account = byId.get(kind === 'transfer' ? v.fromAccountId : v.accountId);
  const toAccount = byId.get(v.toAccountId);
  const currencyOptions = useMemo(() => {
    const own = currenciesOf(account);
    const options =
      kind === 'transfer' ? own.filter((c) => currenciesOf(toAccount).includes(c)) : own;
    return sortCurrencies(options, baseCurrency);
  }, [account, toAccount, kind, baseCurrency]);

  // Keep the currency one the chosen account(s) hold; prefer the one used last there.
  const accountKey = account?.id ?? '';
  useEffect(() => {
    const preferred = preferredCurrency(currencyOptions, accountKey, defaults);
    if (!currencyOptions.includes(form.getValues('currency')) && preferred) {
      form.setValue('currency', preferred);
    }
  }, [currencyOptions, accountKey, defaults, form]);

  const currency = v.currency;
  const foreign = currency !== baseCurrency;
  const askRate = foreign && (rateWanted || latestRates[currency] === undefined);
  const resolver = useMemo(() => zodResolver(addFormSchema({ kind, askRate })), [kind, askRate]);
  // The form reads the resolver through a ref: whether the rate field is shown changes the checks.
  useEffect(() => {
    resolverRef.current = resolver;
  }, [resolver]);

  const create = useMutation({
    mutationFn: (body: CreateTransactionRequest) =>
      correcting
        ? api.transactions.correct(correcting.id, body, { idempotencyKey })
        : api.transactions.create(body, { idempotencyKey }),
    onSuccess: async () => {
      renewKey();
      await invalidate();
      navigate('/', { state: { saved: true } });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'rate.required') setRateWanted(true);
    },
  });

  const amount = parsePositive(v.amount ?? '', currency);
  const rate = askRate ? parseRateInput(v.rate ?? '') : (latestRates[currency] ?? null);
  const remaining = v.split ? splitRemaining(v, currency) : null;
  const errors = form.formState.errors;
  // A check on the whole split (lines must add up) lands on `lines` or `lines.root`.
  const linesError = [errors.lines?.root, errors.lines as FieldError | undefined].find(
    (e) => typeof e?.message === 'string',
  );
  const errorText = (error: FieldError | undefined) =>
    error?.message ? t(error.message, { currency }) : null;

  const onCredit = v.credit ? creditRemaining(v, currency) : null;
  const reformat = (name: 'amount' | 'paidNow' | `lines.${number}.amount`) => {
    const parsed =
      name === 'paidNow'
        ? parseNonNegative(form.getValues(name), currency)
        : parsePositive(form.getValues(name), currency);
    if (parsed) form.setValue(name, groupAmountInput(parsed, currency));
  };

  // Switching currency keeps what was typed, written the new currency's way (no LBP decimals).
  const changeCurrency = (next: string) => {
    const typed = parsePositive(form.getValues('amount'), currency);
    form.setValue('currency', next);
    if (typed && parsePositive(typed, next)) form.setValue('amount', groupAmountInput(typed, next));
  };

  const accountOptions = (exclude?: string): PickerOption[] =>
    accounts
      .filter((a) => a.id !== exclude)
      .map((a) => ({
        value: a.id,
        label: a.name,
        hint: [
          a.ownerMemberId === null ? t('accountDetail.shared') : (a.ownerDisplayName ?? ''),
          a.balances.map((b) => formatAmount(b.balance, b.currency, i18n.language)).join(' · '),
        ]
          .filter(Boolean)
          .join(' · '),
      }));

  const startSplit = () => {
    form.setValue('split', true);
    lines.replace([
      { categoryId: v.categoryId, amount: v.amount, note: '' },
      { categoryId: '', amount: '', note: '' },
    ]);
  };
  const stopSplit = () => {
    form.setValue('split', false);
    form.setValue('categoryId', v.lines[0]?.categoryId ?? v.categoryId);
    lines.replace([]);
  };

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((body) =>
        create.mutate(
          prefill
            ? { ...body, recurring: { templateId: prefill.template.id, dueOn: prefill.dueOn } }
            : body,
        ),
      )}
    >
      <Card className="flex flex-col items-center gap-3">
        <label htmlFor="add-amount" className="sr-only">
          {t('add.amount')}
        </label>
        <input
          id="add-amount"
          inputMode={currency === 'LBP' ? 'text' : 'decimal'}
          autoComplete="off"
          dir="ltr"
          placeholder={currency === 'LBP' ? '500k' : '0.00'}
          aria-invalid={errors.amount ? true : undefined}
          aria-describedby="add-amount-help"
          className="w-full bg-transparent text-center text-[46px] leading-tight font-semibold tracking-[-1px] tabular-nums placeholder:text-muted focus-visible:outline-none"
          {...form.register('amount', { onBlur: () => reformat('amount') })}
        />
        {currencyOptions.length > 1 ? (
          <div className="w-40">
            <Segmented
              label={t('add.currency')}
              options={currencyOptions.map((c) => ({ value: c, label: c }))}
              value={currency}
              onChange={changeCurrency}
            />
          </div>
        ) : currencyOptions.length === 1 ? (
          <span className="rounded-full bg-surface-2 px-3 py-1 text-sm font-semibold">
            {currency}
          </span>
        ) : null}
        <div
          id="add-amount-help"
          className="flex flex-col items-center text-center"
          aria-live="polite"
        >
          <AmountPreview
            raw={v.amount ?? ''}
            currency={currency}
            baseCurrency={baseCurrency}
            rate={rate}
          />
          {currency === 'LBP' && !amount && (
            <span className="text-xs text-muted">{t('add.fastEntry')}</span>
          )}
          {errors.amount && <span className="text-sm text-danger">{errorText(errors.amount)}</span>}
        </div>
      </Card>

      {kind === 'transfer' && currencyOptions.length === 0 && (
        <p
          role="alert"
          className="rounded-xl border border-danger bg-surface p-3 text-sm text-danger"
        >
          {t('add.noCommonCurrency')}
        </p>
      )}

      {foreign && (
        <div className="flex flex-col gap-2">
          <StaleRateNotice />
          {!askRate && latestRates[currency] && (
            <p className="px-1 text-xs text-muted">
              {t('rates.usesFamilyRate', {
                base: baseCurrency,
                rate: formatRateValue(latestRates[currency], i18n.language),
                quote: currency,
              })}{' '}
              <button
                type="button"
                className="min-h-11 font-medium text-accent underline-offset-2 hover:underline"
                onClick={() => setRateWanted(true)}
              >
                {t('add.otherRate')}
              </button>
            </p>
          )}
        </div>
      )}

      <ListGroup>
        {kind !== 'transfer' && (
          <PayeeRow
            kind={kind}
            value={v.payeeName ?? ''}
            focused={payeeFocused}
            onFocus={() => setPayeeFocused(true)}
            onBlur={() => setTimeout(() => setPayeeFocused(false), 150)}
            register={form.register('payeeName')}
            onPick={(payee) => {
              form.setValue('payeeName', payee.name);
              setPayeeFocused(false);
              if (payee.suggestedCategoryId && !categoryTouched && !v.split) {
                form.setValue('categoryId', payee.suggestedCategoryId);
              }
            }}
          />
        )}
        {kind !== 'transfer' && !v.split && (
          <Row label={t('add.category')} error={errorText(errors.categoryId)}>
            <CategoryChip
              kind={kind}
              id={v.categoryId}
              onClick={() => setPicker({ type: 'category', line: null })}
            />
          </Row>
        )}
        {kind === 'transfer' ? (
          <>
            <Row label={t('add.from')} error={errorText(errors.fromAccountId)}>
              <Chip onClick={() => setPicker({ type: 'account', field: 'fromAccountId' })}>
                {byId.get(v.fromAccountId)?.name ?? t('add.pickAccount')}
              </Chip>
            </Row>
            <SwapRow
              label={t('add.swap')}
              onSwap={() => {
                const { fromAccountId, toAccountId } = form.getValues();
                form.setValue('fromAccountId', toAccountId);
                form.setValue('toAccountId', fromAccountId);
              }}
            />
            <Row label={t('add.to')} error={errorText(errors.toAccountId)}>
              <Chip onClick={() => setPicker({ type: 'account', field: 'toAccountId' })}>
                {byId.get(v.toAccountId)?.name ?? t('add.pickAccount')}
              </Chip>
            </Row>
          </>
        ) : (
          <Row label={t('add.account')} error={errorText(errors.accountId)}>
            <Chip onClick={() => setPicker({ type: 'account', field: 'accountId' })}>
              {account?.name ?? t('add.pickAccount')}
            </Chip>
          </Row>
        )}
        <Row label={t('add.date')} htmlFor="add-date" error={errorText(errors.occurredOn)}>
          <input
            id="add-date"
            type="date"
            max={today}
            className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
            {...form.register('occurredOn')}
          />
        </Row>
        <Row label={t('add.note')} htmlFor="add-note">
          <input
            id="add-note"
            autoComplete="off"
            maxLength={200}
            placeholder={t('add.notePlaceholder')}
            className="min-h-11 w-full min-w-0 bg-transparent text-end text-[15px] placeholder:text-muted focus-visible:outline-none"
            {...form.register('description')}
          />
        </Row>
      </ListGroup>

      {kind !== 'transfer' && !prefill && (
        <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-3xl border border-line bg-surface px-4 py-3">
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="font-medium">{t('credit.toggle')}</span>
            <span id="credit-toggle-hint" className="text-sm text-muted">
              {t(kind === 'expense' ? 'credit.toggleHintExpense' : 'credit.toggleHintIncome')}
            </span>
          </span>
          <input
            type="checkbox"
            role="switch"
            aria-label={t('credit.toggle')}
            aria-describedby="credit-toggle-hint"
            className="size-5 shrink-0 accent-accent"
            {...form.register('credit')}
          />
        </label>
      )}

      {kind !== 'transfer' && v.credit && (
        <section className="flex flex-col gap-2" aria-label={t('credit.toggle')}>
          <ListGroup>
            <Row
              label={t(kind === 'expense' ? 'credit.paidNow' : 'credit.receivedNow')}
              htmlFor="credit-paid-now"
              error={errorText(errors.paidNow)}
            >
              <input
                id="credit-paid-now"
                inputMode={currency === 'LBP' ? 'text' : 'decimal'}
                autoComplete="off"
                dir="ltr"
                placeholder="0"
                aria-invalid={errors.paidNow ? true : undefined}
                className="min-h-11 w-full min-w-0 bg-transparent text-end text-[15px] tabular-nums placeholder:text-muted focus-visible:outline-none"
                {...form.register('paidNow', { onBlur: () => reformat('paidNow') })}
              />
            </Row>
            <li className="flex min-h-12 items-center justify-between gap-3 px-4 py-2">
              <span className="text-[13px] text-muted">{t('credit.onCredit')}</span>
              <output
                dir="ltr"
                aria-live="polite"
                className="text-[15px] font-semibold tabular-nums"
              >
                {onCredit === null ? '–' : formatAmount(onCredit, currency, i18n.language)}
              </output>
            </li>
            <Row
              label={t(kind === 'expense' ? 'credit.owedTo' : 'credit.owedBy')}
              error={errorText(errors.personId)}
            >
              <Chip onClick={() => setPersonPicker(true)}>
                {personLabel || t('debts.pickPerson')}
              </Chip>
            </Row>
            <Row label={t('credit.dueOn')} htmlFor="credit-due">
              <input
                id="credit-due"
                type="date"
                className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
                {...form.register('dueOn')}
              />
            </Row>
          </ListGroup>
          <p className="px-1 text-xs text-muted">
            {t(kind === 'expense' ? 'credit.hintExpense' : 'credit.hintIncome')}
          </p>
        </section>
      )}

      {kind !== 'transfer' && v.split && (
        <section className="flex flex-col gap-2" aria-label={t('add.splitTitle')}>
          <ListGroup>
            {lines.fields.map((field, i) => (
              <li key={field.id} className="flex flex-col gap-2 px-4 py-3">
                <div className="flex items-center gap-2">
                  <CategoryChip
                    kind={kind}
                    id={v.lines[i]?.categoryId ?? ''}
                    onClick={() => setPicker({ type: 'category', line: i })}
                  />
                  <input
                    aria-label={t('add.lineAmount', { n: i + 1 })}
                    inputMode={currency === 'LBP' ? 'text' : 'decimal'}
                    autoComplete="off"
                    dir="ltr"
                    placeholder={currency === 'LBP' ? '100k' : '0.00'}
                    className="min-h-11 w-full min-w-0 flex-1 rounded-2xl border border-line bg-surface px-3 text-end text-[15px] tabular-nums focus-visible:outline-2 focus-visible:outline-accent"
                    {...form.register(`lines.${i}.amount`, {
                      onBlur: () => reformat(`lines.${i}.amount`),
                    })}
                  />
                  {lines.fields.length > 1 && (
                    <button
                      type="button"
                      aria-label={t('add.removeLine', { n: i + 1 })}
                      onClick={() => lines.remove(i)}
                      className="flex size-11 shrink-0 items-center justify-center rounded-2xl text-muted hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
                    >
                      <Trash2 aria-hidden="true" className="size-5" strokeWidth={2} />
                    </button>
                  )}
                </div>
                <input
                  aria-label={t('add.lineNote', { n: i + 1 })}
                  autoComplete="off"
                  maxLength={200}
                  placeholder={t('add.lineNotePlaceholder')}
                  className="min-h-11 w-full bg-transparent text-[13px] placeholder:text-muted focus-visible:outline-none"
                  {...form.register(`lines.${i}.note`)}
                />
                {(errors.lines?.[i]?.categoryId || errors.lines?.[i]?.amount) && (
                  <p className="text-sm text-danger">
                    {errorText(errors.lines?.[i]?.categoryId ?? errors.lines?.[i]?.amount)}
                  </p>
                )}
              </li>
            ))}
          </ListGroup>
          <div className="flex items-center justify-between gap-3 px-1">
            <Button
              variant="ghost"
              onClick={() => lines.append({ categoryId: '', amount: '', note: '' })}
              disabled={lines.fields.length >= 20}
            >
              <Plus aria-hidden="true" className="size-5" strokeWidth={2} />
              {t('add.addLine')}
            </Button>
            <span
              aria-live="polite"
              className={cn(
                'text-[13px] tabular-nums',
                remaining !== null && remaining !== '0.0000' ? 'text-danger' : 'text-muted',
              )}
            >
              {remaining === null
                ? null
                : remaining === '0.0000'
                  ? t('add.splitBalanced')
                  : t('add.remaining', {
                      amount: formatAmount(remaining, currency, i18n.language),
                    })}
            </span>
          </div>
          {linesError && <p className="px-1 text-sm text-danger">{errorText(linesError)}</p>}
        </section>
      )}

      {kind !== 'transfer' && (
        <Button variant="secondary" onClick={v.split ? stopSplit : startSplit}>
          {v.split ? t('add.unsplit') : t('add.split')}
        </Button>
      )}

      {kind !== 'transfer' && account?.isPrivate && (
        <label className="flex min-h-11 items-start gap-3 px-1">
          <input
            type="checkbox"
            className="mt-1 size-5 accent-accent"
            {...form.register('sharedWithFamily')}
          />
          <span className="flex flex-col">
            <span className="font-medium">{t('add.share')}</span>
            <span className="text-sm text-muted">{t('add.shareHint')}</span>
          </span>
        </label>
      )}

      {askRate && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="add-rate" className="text-[13px] font-medium text-muted">
            {t('rates.oneOffLabel', { base: baseCurrency, quote: currency })}
          </label>
          <Input
            id="add-rate"
            inputMode="decimal"
            autoComplete="off"
            dir="ltr"
            placeholder="89,500"
            aria-invalid={errors.rate ? true : undefined}
            {...form.register('rate')}
          />
          <p className="text-xs text-muted">
            {latestRates[currency] ? t('add.oneOffHint') : t('rates.oneOffHint')}
          </p>
          {errors.rate && <p className="text-sm text-danger">{errorText(errors.rate)}</p>}
        </div>
      )}

      {kind === 'expense' && (
        <LimitWarning
          memberId={myId}
          amount={amount}
          currency={currency}
          baseCurrency={baseCurrency}
          rate={rate}
        />
      )}
      <FormError error={create.error} />
      <Button
        type="submit"
        disabled={
          create.isPending ||
          currencyOptions.length === 0 ||
          (v.split && remaining !== null && remaining !== '0.0000')
        }
      >
        {create.isPending ? t('common.saving') : t('add.save')}
      </Button>
      <CurrencyLockNotice />

      {picker?.type === 'account' && (
        <PickerSheet
          title={t(picker.field === 'toAccountId' ? 'add.pickTo' : 'add.pickAccount')}
          options={accountOptions(
            picker.field === 'toAccountId'
              ? v.fromAccountId
              : picker.field === 'fromAccountId'
                ? v.toAccountId
                : undefined,
          )}
          selected={v[picker.field]}
          onPick={(id) => {
            form.setValue(picker.field, id);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {personPicker && (
        <PersonPicker
          members={[]}
          canAdd={canAddPeople}
          selected={v.personId}
          onPickPerson={(id, name) => {
            form.setValue('personId', id);
            form.setValue('personName', '');
            setPersonLabel(name);
            setPersonPicker(false);
          }}
          onPickName={(name) => {
            form.setValue('personId', '');
            form.setValue('personName', name);
            setPersonLabel(name);
            setPersonPicker(false);
          }}
          onPickMember={() => setPersonPicker(false)}
          onClose={() => setPersonPicker(false)}
        />
      )}
      {picker?.type === 'category' && kind !== 'transfer' && (
        <CategoryPicker
          kind={kind}
          selected={picker.line === null ? v.categoryId : (v.lines[picker.line]?.categoryId ?? '')}
          onPick={(id) => {
            if (picker.line === null) {
              form.setValue('categoryId', id);
              setCategoryTouched(true);
            } else {
              form.setValue(`lines.${picker.line}.categoryId`, id);
            }
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </form>
  );
}

export function CategoryChip({
  kind,
  id,
  onClick,
}: {
  kind: 'expense' | 'income';
  id: string;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const categories = useCategories(kind, false);
  const name = categories.data?.items.find((c) => c.id === id)?.name;
  return <Chip onClick={onClick}>{name ?? t('add.pickCategory')}</Chip>;
}

/** Search as you type, most used first, "Create …" at the bottom for those who may. */
export function CategoryPicker({
  kind,
  selected,
  onPick,
  onClose,
}: {
  kind: 'expense' | 'income';
  selected: string;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const categories = useCategories(kind, false);
  const canManage = useCan('categories.manage');
  const queryClient = useQueryClient();
  const [key, renewKey] = useIdempotencyKey();
  const create = useMutation({
    mutationFn: (name: string) => api.categories.create({ name, kind }, { idempotencyKey: key }),
    onSuccess: async (category: Category) => {
      renewKey();
      await queryClient.invalidateQueries({ queryKey: categoriesKey });
      onPick(category.id);
    },
  });
  const needle = q.trim().toLowerCase();
  const items = (categories.data?.items ?? []).filter((c) => c.name.toLowerCase().includes(needle));
  const exact = items.some((c) => c.name.toLowerCase() === needle);
  return (
    <PickerSheet
      title={t('add.pickCategory')}
      search={{ value: q, onChange: setQ, label: t('categories.search') }}
      options={items.map((c) => ({ value: c.id, label: c.name }))}
      selected={selected}
      onPick={onPick}
      onClose={onClose}
      empty={categories.isPending ? t('app.loading') : t('categories.none')}
      footer={
        needle && !exact ? (
          canManage ? (
            <div className="flex flex-col gap-2">
              <FormError error={create.error} />
              <Button
                variant="secondary"
                disabled={create.isPending}
                onClick={() => create.mutate(q.trim())}
              >
                <Plus aria-hidden="true" className="size-5" strokeWidth={2} />
                {t('add.createCategory', { name: q.trim() })}
              </Button>
            </div>
          ) : (
            <p className="px-1 text-sm text-muted">{t('add.askParentCategory')}</p>
          )
        ) : null
      }
    />
  );
}

/** Payee name with suggestions from what the viewer can see; picking one pre-fills the category. */
export function PayeeRow({
  kind,
  value,
  focused,
  onFocus,
  onBlur,
  register,
  onPick,
}: {
  kind: 'expense' | 'income';
  value: string;
  focused: boolean;
  onFocus: () => void;
  onBlur: () => void;
  register: ReturnType<ReturnType<typeof useForm<AddFormValues>>['register']>;
  onPick: (payee: Payee) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value.trim()), 200);
    return () => clearTimeout(timer);
  }, [value]);
  const payees = usePayees(debounced);
  const suggestions = (payees.data?.items ?? []).filter(
    (p) => p.name.toLowerCase() !== value.trim().toLowerCase(),
  );
  const open = focused && suggestions.length > 0;
  return (
    <li className="flex flex-col px-4 py-2">
      <div className="flex min-h-12 items-center justify-between gap-3">
        <label htmlFor={id} className="shrink-0 text-[13px] text-muted">
          {kind === 'expense' ? t('add.payee') : t('add.payer')}
        </label>
        <input
          id={id}
          autoComplete="off"
          maxLength={80}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          placeholder={t('add.payeePlaceholder')}
          className="min-h-11 w-full min-w-0 bg-transparent text-end text-[15px] placeholder:text-muted focus-visible:outline-none"
          {...register}
          onFocus={onFocus}
          onBlur={(e) => {
            void register.onBlur(e);
            onBlur();
          }}
        />
      </div>
      {open && (
        <ul
          id={`${id}-list`}
          role="listbox"
          aria-label={t('add.payeeSuggestions')}
          className="flex flex-wrap gap-2 pb-2"
        >
          {suggestions.slice(0, 5).map((p) => (
            <li key={p.id} role="option" aria-selected={false}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onPick(p)}
                className="min-h-11 rounded-full bg-surface-2 px-4 text-sm font-medium hover:bg-line focus-visible:outline-2 focus-visible:outline-accent"
              >
                {p.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
